/**
 * Shared metadata utilities for consistent Open Graph and Twitter cards.
 *
 * Provides helper functions to generate metadata with proper OG images,
 * Twitter cards, and article-specific tags.
 */
import type { Metadata } from 'next';

/** Base URL for absolute URLs in metadata (www subdomain is canonical) */
export const SITE_URL = 'https://www.halcy.ai';

/** Site name for Open Graph */
export const SITE_NAME = 'Halcy';

/** Default author for articles */
export const DEFAULT_AUTHOR = 'Halcy';

/** Default publish date for existing content (site launch) */
export const DEFAULT_PUBLISH_DATE = '2025-01-10T00:00:00Z';

/**
 * Common Open Graph configuration shared across all pages.
 * The image is automatically generated by opengraph-image.tsx.
 */
export function getBaseOpenGraph(): Metadata['openGraph'] {
  return {
    siteName: SITE_NAME,
    locale: 'en_US',
  };
}

/**
 * Common Twitter card configuration.
 * Uses summary_large_image for better visual presence.
 * The image is automatically generated by twitter-image.tsx.
 */
export function getBaseTwitter(): Metadata['twitter'] {
  return {
    card: 'summary_large_image',
    site: '@halcyai',
  };
}

interface WebsiteMetadataOptions {
  title: string;
  description: string;
  path: string;
}

/**
 * Generate metadata for website-type pages (homepage, hub pages).
 */
export function createWebsiteMetadata({
  title,
  description,
  path,
}: WebsiteMetadataOptions): Metadata {
  const url = `${SITE_URL}${path}`;

  return {
    title,
    description,
    alternates: {
      canonical: path,
    },
    openGraph: {
      ...getBaseOpenGraph(),
      title,
      description,
      type: 'website',
      url,
    },
    twitter: {
      ...getBaseTwitter(),
      title,
      description,
    },
  };
}

interface ArticleMetadataOptions {
  title: string;
  description: string;
  path: string;
  publishedTime?: string;
  modifiedTime?: string;
  authors?: string[];
}

/**
 * Generate metadata for article-type pages (concept pages, market pages, problem pages).
 */
export function createArticleMetadata({
  title,
  description,
  path,
  publishedTime = DEFAULT_PUBLISH_DATE,
  modifiedTime,
  authors = [DEFAULT_AUTHOR],
}: ArticleMetadataOptions): Metadata {
  const url = `${SITE_URL}${path}`;

  return {
    title,
    description,
    alternates: {
      canonical: path,
    },
    openGraph: {
      ...getBaseOpenGraph(),
      title,
      description,
      type: 'article',
      url,
      publishedTime,
      modifiedTime,
      authors,
    },
    twitter: {
      ...getBaseTwitter(),
      title,
      description,
    },
  };
}
